import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, Users, Clock, MousePointerClick, TrendingUp, TrendingDown,
  Eye, ArrowUpRight, Settings, Save, ExternalLink, AlertCircle, Phone,
  FileText, Car, Calendar, RefreshCw, MapPin, Globe, Send, Megaphone
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const AdminAnalytics = () => {
  const { getAuthHeader } = useAuth();
  const [period, setPeriod] = useState('7d');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gaId, setGaId] = useState('');
  const [savedGaId, setSavedGaId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/analytics`, {
        headers: getAuthHeader(),
      });
      if (response.data.ga_measurement_id) {
        setGaId(response.data.ga_measurement_id);
        setSavedGaId(response.data.ga_measurement_id);
      }
    } catch (error) {
      console.error('Error fetching analytics settings:', error);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard?period=${period}`, {
        headers: getAuthHeader(),
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analytiques');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/analytics`, {
        ga_measurement_id: gaId
      }, {
        headers: getAuthHeader(),
      });
      setSavedGaId(gaId);
      toast.success('Google Analytics ID sauvegardé!');
      setShowSetup(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const summary = dashboardData?.summary || {};
  const topVehicles = dashboardData?.top_vehicles || [];
  const dailyChart = dashboardData?.daily_chart || [];
  const ctaBreakdown = dashboardData?.cta_breakdown || {};
  const pageBreakdown = dashboardData?.page_breakdown || {};
  const hourlyDistribution = dashboardData?.hourly_distribution || {};
  const trafficSources = dashboardData?.traffic_sources || [];
  const topCities = dashboardData?.top_cities || [];
  const topCampaigns = dashboardData?.top_campaigns || [];

  // Find peak hour
  const peakHour = Object.entries(hourlyDistribution).sort((a, b) => b[1] - a[1])[0];

  // Send daily report manually
  const sendDailyReport = async () => {
    setSendingReport(true);
    try {
      const response = await axios.post(`${API}/admin/analytics/send-report`, {}, {
        headers: getAuthHeader(),
      });
      if (response.data.success) {
        toast.success(`Rapport envoyé à ${response.data.sent_to}`);
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du rapport');
    } finally {
      setSendingReport(false);
    }
  };

  // KPI stats
  const stats = [
    { 
      label: 'Pages vues', 
      value: summary.total_page_views?.toLocaleString() || '0', 
      icon: Eye, 
      color: 'bg-blue-50 text-blue-600',
      description: `${summary.vehicle_views || 0} sur véhicules`
    },
    { 
      label: 'Sessions', 
      value: summary.unique_sessions?.toLocaleString() || '0', 
      icon: Users, 
      color: 'bg-green-50 text-green-600',
      description: 'Visiteurs uniques'
    },
    { 
      label: 'Clics CTA', 
      value: summary.cta_clicks?.toLocaleString() || '0', 
      icon: MousePointerClick, 
      color: 'bg-purple-50 text-purple-600',
      description: `${summary.call_clicks || 0} appels`
    },
    { 
      label: 'Demandes', 
      value: (summary.inquiries + summary.test_drives)?.toLocaleString() || '0', 
      icon: FileText, 
      color: 'bg-orange-50 text-orange-600',
      description: `${summary.conversion_rate || 0}% conversion`
    },
  ];

  // Page breakdown for chart
  const topPages = Object.entries(pageBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([page, views]) => ({
      page,
      name: page === 'homepage' ? 'Accueil' : 
            page === 'inventory' ? 'Inventaire' : 
            page === 'vehicle_detail' ? 'Détails véhicule' :
            page === 'contact' ? 'Contact' :
            page === 'credit' ? 'Demande crédit' : page,
      views,
      percentage: summary.total_page_views > 0 ? Math.round((views / summary.total_page_views) * 100) : 0
    }));

  // CTA breakdown
  const ctaStats = Object.entries(ctaBreakdown)
    .sort((a, b) => b[1] - a[1])
    .map(([element, clicks]) => ({
      element,
      name: element === 'call_button' ? 'Bouton Appeler' :
            element === 'financing_button' ? 'Options financement' :
            element === 'inquiry_form' ? 'Formulaire demande' :
            element === 'test_drive_form' ? 'Essai routier' : element,
      clicks,
      percentage: summary.cta_clicks > 0 ? Math.round((clicks / summary.cta_clicks) * 100) : 0,
      color: element === 'call_button' ? 'bg-green-500' : 
             element === 'financing_button' ? 'bg-blue-500' : 'bg-purple-500'
    }));

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-analytics">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Analytiques
            </h1>
            <p className="text-slate-500 text-xs">Statistiques et performances du site</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Dernières 24h</SelectItem>
                <SelectItem value="7d">7 derniers jours</SelectItem>
                <SelectItem value="30d">30 derniers jours</SelectItem>
                <SelectItem value="90d">90 derniers jours</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchDashboard} variant="outline" size="sm" className="text-xs h-8" disabled={loading}>
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button onClick={sendDailyReport} variant="outline" size="sm" className="text-xs h-8" disabled={sendingReport}>
              <Send className={`h-3 w-3 mr-1 ${sendingReport ? 'animate-pulse' : ''}`} />
              {sendingReport ? 'Envoi...' : 'Envoyer rapport'}
            </Button>
            <Button onClick={() => setShowSetup(!showSetup)} variant="outline" size="sm" className="text-xs h-8">
              <Settings className="h-3 w-3 mr-1" />
              GA
            </Button>
          </div>
        </div>

        {/* GA Setup Panel */}
        {showSetup && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Settings className="h-4 w-4 text-blue-600" />
              Configuration Google Analytics (optionnel)
            </h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="ga-id" className="text-xs text-slate-600">Measurement ID (GA4)</Label>
                <div className="flex gap-2 mt-1">
                  <Input id="ga-id" value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" className="max-w-xs text-xs h-8" />
                  <Button onClick={saveSettings} disabled={saving || !gaId} size="sm" className="text-xs h-8">
                    <Save className="h-3 w-3 mr-1" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </div>
              </div>
              {savedGaId && (
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-green-700 text-xs flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Configuré: <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">{savedGaId}</code>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-500 text-[10px] uppercase tracking-wide">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-800 mt-0.5">{loading ? '...' : stat.value}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{stat.description}</p>
                </div>
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-4 w-4" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Top Viewed Vehicles */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Car className="h-4 w-4 text-blue-600" />
              Véhicules les plus consultés
            </h3>
            {topVehicles.length > 0 ? (
              <div className="space-y-3">
                {topVehicles.slice(0, 5).map((vehicle, index) => (
                  <div key={vehicle.id} className="flex items-center gap-3">
                    <span className="text-slate-400 text-xs font-medium w-5">{index + 1}.</span>
                    <div className="w-12 h-9 rounded overflow-hidden bg-slate-100 flex-shrink-0">
                      {vehicle.images?.[0] ? (
                        <img 
                          src={getImageUrl(vehicle.images[0])} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Car className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 text-xs font-medium truncate">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                      <p className="text-slate-400 text-[10px]">{vehicle.views} vues</p>
                    </div>
                    <div className="text-right">
                      <span className="text-blue-600 font-semibold text-sm">{vehicle.views}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Eye className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucune donnée disponible</p>
              </div>
            )}
          </div>

          {/* Top Pages */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-600" />
              Pages les plus visitées
            </h3>
            {topPages.length > 0 ? (
              <div className="space-y-3">
                {topPages.map((page, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-slate-400 text-xs w-5">{index + 1}.</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-700 text-xs font-medium">{page.name}</span>
                        <span className="text-slate-500 text-[10px]">{page.views.toLocaleString()} vues</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${page.percentage}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucune donnée disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA Performance & Peak Hours */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* CTA Performance */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <MousePointerClick className="h-4 w-4 text-purple-600" />
              Performance des CTA
            </h3>
            {ctaStats.length > 0 ? (
              <div className="space-y-3">
                {ctaStats.map((cta, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${cta.color}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-700 text-xs">{cta.name}</span>
                        <span className="text-slate-500 text-[10px]">{cta.clicks} clics ({cta.percentage}%)</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${cta.color} rounded-full transition-all`} style={{ width: `${cta.percentage}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MousePointerClick className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucun clic enregistré</p>
              </div>
            )}
          </div>

          {/* Peak Hours & Quick Stats */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Statistiques rapides
            </h3>
            <div className="space-y-4">
              {/* Peak Hour */}
              {peakHour && (
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-orange-600 text-[10px] uppercase tracking-wide mb-1">Heure de pointe</p>
                  <p className="text-orange-800 text-lg font-bold">{peakHour[0]}h00 - {parseInt(peakHour[0]) + 1}h00</p>
                  <p className="text-orange-600 text-xs">{peakHour[1]} visites</p>
                </div>
              )}
              
              {/* Conversion Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-green-600 text-[10px] uppercase tracking-wide mb-1">Demandes info</p>
                  <p className="text-green-800 text-xl font-bold">{summary.inquiries || 0}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-blue-600 text-[10px] uppercase tracking-wide mb-1">Essais routiers</p>
                  <p className="text-blue-800 text-xl font-bold">{summary.test_drives || 0}</p>
                </div>
              </div>
              
              {/* Conversion Rate */}
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-[10px] uppercase tracking-wide mb-1">Taux de conversion</p>
                    <p className="text-purple-800 text-2xl font-bold">{summary.conversion_rate || 0}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-300" />
                </div>
                <p className="text-purple-600 text-[10px] mt-1">Vues véhicule → Demande</p>
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Sources & Customer Locations */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Traffic Sources */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4 text-indigo-600" />
              Sources de trafic
            </h3>
            {trafficSources.length > 0 ? (
              <div className="space-y-3">
                {trafficSources.slice(0, 8).map((source, index) => {
                  const maxCount = trafficSources[0]?.count || 1;
                  const percentage = Math.round((source.count / maxCount) * 100);
                  const colors = [
                    'bg-indigo-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
                    'bg-orange-500', 'bg-red-500', 'bg-purple-500', 'bg-pink-500'
                  ];
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${colors[index % colors.length]}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-slate-700 text-xs font-medium">{source.source || 'Direct'}</span>
                          <span className="text-slate-500 text-[10px]">{source.count} sessions</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${colors[index % colors.length]} rounded-full transition-all`} style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucune donnée disponible</p>
              </div>
            )}
          </div>

          {/* Customer Locations */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-red-600" />
              Localisation des visiteurs
            </h3>
            {topCities.length > 0 ? (
              <div className="space-y-3">
                {topCities.slice(0, 8).map((city, index) => {
                  const maxCount = topCities[0]?.count || 1;
                  const percentage = Math.round((city.count / maxCount) * 100);
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs font-medium w-5">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-slate-700 text-xs font-medium">
                            {city.city}{city.region ? `, ${city.region}` : ''}
                          </span>
                          <span className="text-slate-500 text-[10px]">{city.count} visiteurs</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucune donnée de localisation</p>
              </div>
            )}
          </div>
        </div>

        {/* Campaigns */}
        {topCampaigns.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-amber-600" />
              Campagnes marketing (UTM)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {topCampaigns.slice(0, 8).map((campaign, index) => (
                <div key={index} className="bg-amber-50 rounded-lg p-3">
                  <p className="text-amber-800 font-semibold text-sm truncate" title={campaign.campaign}>
                    {campaign.campaign}
                  </p>
                  <p className="text-amber-600 text-xs mt-1">{campaign.count} sessions</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Activity Chart (Simple) */}
        {dailyChart.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              Activité quotidienne
            </h3>
            <div className="flex items-end gap-1 h-32">
              {dailyChart.slice(-14).map((day, index) => {
                const maxViews = Math.max(...dailyChart.map(d => d.views || 0), 1);
                const height = ((day.views || 0) / maxViews) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${day.date}: ${day.views} vues, ${day.clicks} clics`}
                    />
                    <span className="text-[8px] text-slate-400 rotate-45 origin-left">
                      {day.date?.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-[10px]">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded" />
                <span className="text-slate-500">Pages vues</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
